/**
 * WindTiltPage — Lemckert 2008 wind-driven plume centre-line tilt
 *
 * Surface boil shifts downwind from the module position by approximately:
 *   Δx = H_eff · tan(arctan(C_w · U10 / v_plume))
 * with C_w = 0.15 (calibrated to fountain-like upwelling experiments)
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Wind, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import { specFromLegacy, type LegacyModSpec, bubbleDiameterMm, bubbleRiseVelMs } from '@/lib/diffuserPhysics';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

interface ModSpec extends LegacyModSpec { waterDepthM: number; mountingHeightM: number; waterTempC: number; }
const MOD_DEFAULTS: ModSpec = { diffuserCount: 8, diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300, poreSizeUm: 80, airflowPerDiffNm3h: 1.2, waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12 };

export default function WindTiltPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('wind_tilt');
  const [spec, setSpec] = useState<ModSpec>(() => ({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) }));
  const [windSpeed, setWindSpeed] = useState(4.0);

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === KEYS.DIFFUSER_MODULE) setSpec({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) });
    });
    return () => unsub();
  }, []);

  const result = useMemo(() => {
    const H_eff = Math.max(0.3, spec.waterDepthM - spec.mountingHeightM);
    const dSpec = specFromLegacy(spec);
    const d_b = bubbleDiameterMm(dSpec, spec.waterTempC);
    const v_rise = bubbleRiseVelMs(d_b, spec.waterTempC);
    const v_plume = Math.max(0.1, v_rise * 1.4);             // plume centre-line ~1.4× bubble rise
    const C_w = 0.15;                                          // Lemckert 2008 wind-coupling coefficient
    const tilt_deg = Math.atan2(C_w * windSpeed, v_plume) * 180 / Math.PI;
    const surface_shift_m = H_eff * Math.tan(tilt_deg * Math.PI / 180);
    return { H_eff, tilt_deg, surface_shift_m, v_plume };
  }, [spec, windSpeed]);

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Wind}
        title="Module — Wind-tilt plume correction"
        subtitle="Lemckert 2008: surface boil shifts downwind from module position"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${
              enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'
            }`}>
              <input type="checkbox" checked={enabled} onChange={() => toggle('wind_tilt')} className="accent-emerald-500" />
              Module {enabled ? 'ENABLED' : 'DISABLED'}
            </label>
            <button onClick={() => navigate('/modules')}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Modules
            </button>
          </div>
        }
      />

      <ModuleConflictBanner moduleId="wind_tilt" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Wind speed U10</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{windSpeed.toFixed(1)} m/s</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Plume centreline v</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{result.v_plume.toFixed(2)} m/s</div>
        </div>
        <div className="rounded-xl border-2 border-amber-700 bg-amber-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-amber-300">Plume tilt</div>
          <div className="text-2xl font-bold text-amber-200 mt-1">{result.tilt_deg.toFixed(1)}°</div>
        </div>
        <div className="rounded-xl border-2 border-violet-700 bg-violet-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-violet-300">Surface shift</div>
          <div className="text-2xl font-bold text-violet-200 mt-1">{result.surface_shift_m.toFixed(2)} m</div>
          <div className="text-xs text-slate-500 mt-1">downwind from module</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Wind speed (U10)</div>
        <div className="flex items-center gap-3">
          <input type="range" min={0} max={12} step={0.5} value={windSpeed}
            onChange={e => setWindSpeed(Number(e.target.value))} className="flex-1" />
          <div className="w-16 text-right text-slate-100 font-semibold">{windSpeed.toFixed(1)}</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> What this module does when enabled
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            Lemckert 2008 measured persistent fountain-like upwelling above each diffuser module and
            characterised its deflection under wind shear. The plume centreline tilts with the wind
            because the buoyant flow at the surface is carried by surface drift. The model uses the
            empirical relation tilt ≈ arctan(C_w · U10 / v_plume) with C_w = 0.15.
          </p>
          <p>
            <strong className="text-violet-300">Downstream impact when enabled:</strong> Each module on
            the Plume Coverage map gains a small arrow showing the wind-corrected surface-boil location.
            Sample locations on the Sampling Plan page that target "surface boil" are auto-shifted by the
            tilt offset. Operations engineers can plan visual surface inspections accordingly.
          </p>
          <p>
            <strong className="text-amber-300">Engineering implications:</strong> A tilt of more than 8°
            (typical at 5 m/s wind) means surface boils shift by &gt; 0.4 m from the module position.
            For an array with 6–8 m module spacing, this can cause perceived gaps in coverage near the
            upwind edge.
          </p>
          <p className="italic text-slate-500">References: Lemckert 2008; Thorpe 2004.</p>
        </div>
      </div>
    </div>
  );
}
